import { createSelector } from '@reduxjs/toolkit';

/**
 * Selects the article search parameters from the Redux state.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object} The article search parameters.
 */
export const selectArticleSearchParameters = (state) =>
    state?.articleSearchParameters;

/**
 * Selects the search term from the article search parameters.
 *
 * @param {Object} state - The Redux state.
 * @returns {string} The search term.
 */
export const selectSearchTerm = (state) =>
    state?.articleSearchParameters?.searchTerm;

/**
 * Selects the category names from the article search parameters.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array<string>} The category names.
 */
export const selectCategoryNames = (state) =>
    state?.articleSearchParameters?.categoryNames;

/**
 * Selects the order from the article search parameters.
 *
 * @param {Object} state - The Redux state.
 * @returns {string} The order.
 */
export const selectOrder = (state) =>
    state?.articleSearchParameters?.order;

/**
 * Selects the current page from the article search parameters.
 *
 * @param {Object} state - The Redux state.
 * @returns {number} The current page.
 */
export const selectPage = (state) =>
    state?.articleSearchParameters?.page;

/**
 * Selects the page size from the article search parameters.
 *
 * @param {Object} state - The Redux state.
 * @returns {number} The page size.
 */
export const selectPageSize = (state) =>
    state?.articleSearchParameters?.pageSize;

/**
 * Selects the fetching articles status from the article search parameters.
 *
 * @param {Object} state - The Redux state.
 * @returns {string} The fetching articles status.
 */
export const selectFetchingArticlesStatus = (state) =>
    state?.articleSearchParameters?.fetchingArticlesStatus;

/**
 * Selects the fetching articles counts status from the article search parameters.
 *
 * @param {Object} state - The Redux state.
 * @returns {string} The fetching articles counts status.
 */
export const selectFetchingArticlesCountsStatus = (state) =>
    state?.articleSearchParameters?.fetchingArticlesCountsStatus;

/**
 * Creates a search command for fetching article counts.
 *
 * @returns {Object} The search command for article counts.
 */
export const selectArticlesCountsSearchCommand = createSelector(
    selectArticleSearchParameters,
    (articleSearchParameters) => ({
        searchTerm: articleSearchParameters?.searchTerm,
        categoryNames: articleSearchParameters?.categoryNames,
    })
);

/**
 * Creates a search command for fetching articles.
 *
 * @returns {Object} The search command for articles.
 */
export const selectArticlesSearchCommand = createSelector(
    selectArticleSearchParameters,
    (articleSearchParameters) => ({
        searchTerm: articleSearchParameters?.searchTerm,
        categoryNames: articleSearchParameters?.categoryNames,
        order: articleSearchParameters?.order,
        page: articleSearchParameters?.page,
        pageSize: articleSearchParameters?.pageSize,
        contentClient: articleSearchParameters?.contentClient,
        articleCardType: articleSearchParameters?.articleCardType,
        authorIds: articleSearchParameters?.authorIds,
    })
);

/**
 * Selects the first category name from the category names array.
 *
 * @returns {string} The first category name, or an empty string if none exist.
 */
export const selectFirstCategoryNameParameter = createSelector(
    selectCategoryNames,
    (categoryNames) => {
        const categories = categoryNames || [];
        return categories.length > 0 ? categories[0] : '';
    }
);

/**
 * Creates a query string from the search term and first category name.
 *
 * @returns {string} The query string, or an empty string if no parameters exist.
 */
export const selectArticlesQueryStringParameters = createSelector(
    selectSearchTerm,
    selectFirstCategoryNameParameter,
    (searchTerm, categories) => {
        const parametersForQueryString = { searchTerm, categories };
        const urlParameters = new URLSearchParams();

        Object.entries(parametersForQueryString).forEach(([key, value]) => {
            if (value) {
                urlParameters.append(key, value);
            }
        });

        const queryString = urlParameters.toString();
        return queryString ? `?${queryString}` : '';
    }
);

/**
 * Creates a modifiers object for search results.
 *
 * @returns {Object} The search results modifiers.
 */
export const selectSearchResultsModifiers = createSelector(
    selectSearchTerm,
    selectCategoryNames,
    selectOrder,
    selectPage,
    selectPageSize,
    selectFetchingArticlesStatus,
    (searchTerm, categoryNames, order, page, pageSize, fetchingArticlesStatus) => ({
        searchTerm,
        categoryNames,
        order,
        page,
        pageSize,
        fetchingArticlesStatus,
    })
);

/**
 * Creates a modifiers object for counts results.
 *
 * @returns {Object} The counts results modifiers.
 */
export const selectCountsResultsModifiers = createSelector(
    selectSearchTerm,
    selectCategoryNames,
    selectFetchingArticlesCountsStatus,
    (searchTerm, categoryNames, fetchingArticlesCountsStatus) => ({
        searchTerm,
        categoryNames,
        fetchingArticlesCountsStatus,
    })
);
